import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { CollectionView } from '@casops/components/layout/collection-view/CollectionView';
import { useTableData } from '@casops/shared/hooks/useTableData';
import { Permission, useIsUserWithPermission } from '@casops/permissions';
import { SidePanel } from '@casops/store/slices/globalSlice';
import { ModelName } from '@casops/store/api';
import {
  GeofenceFilters,
  GeofencesListModel,
  useListGeofencesQuery,
} from '@casops/store/apis/geofencesApi';
import CreateItemButton from '@casops/components/inputs/create-item-button/CreateItemButton';
import AssetGroupCellRenderer from '@casops/components/tables/cells/asset-group/AssetGroupCellRenderer';
import {
  useAssetGroupIdFormatter,
  useGeofenceTypesFormatter,
  useStyleIdFormatter,
} from '@casops/components/tables/formatters/idFormatters';
import FilterButton from '@casops/components/inputs/filter-button/FilterButton';
import { GEOFENCE_KEY } from '@casops/navigation';
import useRowFocus from '@casops/components/tables/grid/useRowFocus';
import { useNavigate } from 'react-router-dom';
import ButtonWithIcon from '@casops/components/inputs/button-with-icon/ButtonWithIcon';
import GeofenceStateCellRenderer, {
  geofenceStateComparator,
  geofenceStateValueGetter,
} from '@casops/components/tables/cells/geofence-state/GeofenceStateCellRenderer';
import { FilterGroup } from '@casops/filters/types';
import useFilters from '@casops/filters/useFilters';
import { GEOFENCES_SLICE_KEY } from '@casops/store/slices/gridSlice';

type Model = GeofencesListModel;

const modelName = ModelName.Geofence;
const permission = Permission.GeofenceManagement;

export function Geofences() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const styleIdFormatter = useStyleIdFormatter();
  const assetGroupIdFormatter = useAssetGroupIdFormatter();
  const geofenceTypesFormatter = useGeofenceTypesFormatter();

  const columnDefs = useMemo<ColDef<Model>[]>(() => {
    const columnDefs: ColDef<Model>[] = [
      {
        colId: 'GeofenceNameSearch',
        field: 'name',
        hide: true, // Inlcude the hidden name field for use in the quick filter.
      },
      {
        colId: 'GeofenceName',
        headerName: t('Fields.GeofenceName'),
        valueGetter: geofenceStateValueGetter, // Not searchable
        comparator: geofenceStateComparator, // Enable sorting for custom value.
        cellRenderer: GeofenceStateCellRenderer,
        minWidth: 150,
        maxWidth: 200,
      },
      {
        colId: 'StyleName',
        field: 'stid',
        headerName: t('Fields.StyleName'),
        valueFormatter: styleIdFormatter<Model, 'stid'>,
        minWidth: 120,
        maxWidth: 200,
      },
      {
        colId: 'GeofenceTypes',
        field: 'fts',
        headerName: t('Fields.GeofenceTypes'),
        valueFormatter: geofenceTypesFormatter<Model, 'fts'>,
        minWidth: 120,
        maxWidth: 200,
      },
      {
        colId: 'AssetGroup',
        field: 'ags',
        headerName: t('Fields.AssetGroup'),
        valueFormatter: assetGroupIdFormatter<Model, 'ags'>, // Searchable
        cellRenderer: AssetGroupCellRenderer,
        minWidth: 300,
      },
      { colId: 'Description', field: 'desc', headerName: t('Fields.Description'), minWidth: 300 },
      {
        colId: 'Vertices',
        field: 'vert',
        headerName: t('Fields.Vertices'),
        minWidth: 80,
        maxWidth: 100,
      },
    ];

    return columnDefs;
  }, [t, styleIdFormatter, geofenceTypesFormatter, assetGroupIdFormatter]);

  const noResultsDescription = t('Model.Collection.NoItems', { modelName });

  const geofenceFilters = useFilters<GeofenceFilters>(FilterGroup.Geofences);

  const { data, ...requestRemainder } = useListGeofencesQuery(
    { query: geofenceFilters },
    {
      refetchOnMountOrArgChange: 120,
    },
  );

  const { items, countAll, countFilter } = data ?? {};

  const { hasData, noDataDescription } = useTableData<Model>({
    data: items,
    ...requestRemainder,
    noResultsDescription,
  });

  const { rowClassRules, onRowClick, ensureNodeVisibleCallback, ensureNodeVisibleKey } =
    useRowFocus<Model>(GEOFENCE_KEY);

  const isUserAllowed = useIsUserWithPermission(permission);

  const onCreate = useCallback(() => {
    navigate('/home/geofence/create');
  }, [navigate]);

  return (
    <CollectionView<Model>
      sliceKey={GEOFENCES_SLICE_KEY}
      modelName={modelName}
      columnDefs={columnDefs}
      rowData={items}
      hasData={hasData}
      noDataDescription={noDataDescription}
      showSummaryInTable={true}
      filterProps={{
        filterGroup: FilterGroup.Geofences,
        countAll,
        countFilter,
      }}
      onRowClick={onRowClick}
      rowClassRules={rowClassRules}
      ensureNodeVisibleCallback={ensureNodeVisibleCallback}
      ensureNodeVisibleKey={ensureNodeVisibleKey}
      includeHiddenColumnsInQuickFilter={true}
      actions={
        <>
          {isUserAllowed && <ButtonWithIcon icon="add" label={t('Create')} onClick={onCreate} />}
          <CreateItemButton
            modelName={modelName}
            sidePanel={SidePanel.GeofenceImport}
            permission={permission}
            label={t('Import')}
            icon="arrow-down"
            variant="secondary"
          />
          <FilterButton filters={FilterGroup.Geofences} />
        </>
      }
    />
  );
}

export default Geofences;
